import axios from 'axios';
import type {
    BulkSetupResponse,
    BulkSimulateResponse,
    CreateSetupInput,
    GranulationsResponse,
    ResolveSlugsResponse,
    Run,
    RunResponse,
    SegmentResult,
    Setup,
    SetupBase,
    UpdateSetupInput,
} from '@/types/price-structure';

const PRICE_STRUCTURE_API_URL =
    import.meta.env.VITE_PRICE_STRUCTURE_API_URL || 'http://localhost:8083';

const client = axios.create({
    baseURL: PRICE_STRUCTURE_API_URL,
    headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((config) => {
    const token = localStorage.getItem('access_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

// ========== SETUPS ==========

export const getSetups = async (): Promise<Setup[]> => {
    const { data } = await client.get<Setup[]>('/api/v1/setups');
    return data;
};

export const getSetup = async (setupId: number): Promise<Setup> => {
    const { data } = await client.get<Setup>(`/api/v1/setups/${setupId}`);
    return data;
};

export const getSetupByDomain = async (domainId: number): Promise<Setup> => {
    const { data } = await client.get<Setup>(`/api/v1/setups/domain/${domainId}`);
    return data;
};

export const createSetup = async (input: CreateSetupInput): Promise<Setup> => {
    const { data } = await client.post<Setup>('/api/v1/setups', input);
    return data;
};

export const updateSetup = async (setupId: number, input: UpdateSetupInput): Promise<Setup> => {
    const { data } = await client.put<Setup>(`/api/v1/setups/${setupId}`, input);
    return data;
};

export const deleteSetup = async (setupId: number): Promise<void> => {
    await client.delete(`/api/v1/setups/${setupId}`);
};

/** Cria setups para vários domínios. Conflito num domínio não impede os demais. */
export const createSetupsBulk = async (setups: CreateSetupInput[]): Promise<BulkSetupResponse> => {
    const { data } = await client.post<BulkSetupResponse>('/api/v1/setups/bulk', { setups });
    return data;
};

// ========== EXECUÇÃO ==========

export const runSetup = async (setupId: number): Promise<RunResponse> => {
    const { data } = await client.post<RunResponse>(`/api/v1/setups/${setupId}/run`);
    return data;
};

/** Dry-run com dado real do GAM. Não grava nada. */
export const simulate = async (setup: SetupBase): Promise<RunResponse> => {
    const { data } = await client.post<RunResponse>('/api/v1/simulate', setup);
    return data;
};

/**
 * Dry-run de vários domínios em paralelo (até 6 simultâneos no servidor).
 * Cada domínio leva ~8s, então espere dezenas de segundos com muitas linhas.
 */
export const simulateBulk = async (simulations: SetupBase[]): Promise<BulkSimulateResponse> => {
    const { data } = await client.post<BulkSimulateResponse>('/api/v1/simulate/bulk', {
        simulations,
    });
    return data;
};

/** Traduz ids de post em slugs do WordPress do domínio, para exibição e link. */
export const resolveSlugs = async (
    domainId: number,
    postIds: string[],
): Promise<ResolveSlugsResponse> => {
    const { data } = await client.post<ResolveSlugsResponse>('/api/v1/posts/resolve-slugs', {
        domain_id: domainId,
        post_ids: postIds,
    });
    return data;
};

// ========== RESULTADOS E AUDITORIA ==========

export const getGranulations = async (domainId: number): Promise<GranulationsResponse> => {
    const { data } = await client.get<GranulationsResponse>(
        `/api/v1/granulations/domain/${domainId}`,
    );
    return data;
};

export const getRuns = async (setupId: number): Promise<Run[]> => {
    const { data } = await client.get<Run[]>(`/api/v1/runs/setup/${setupId}`);
    return data;
};

export const getRunSegments = async (runId: number): Promise<SegmentResult[]> => {
    const { data } = await client.get<SegmentResult[]>(`/api/v1/runs/${runId}/segments`);
    return data;
};
